import type { EnemyType } from '../entities/enemyDefs';

/**
 * Overtime enrage (design: the 15-min run timer, progression.ts). Past
 * RUN_TIME_LIMIT_SECONDS the World layers escalating spawn pressure on top of
 * the director's schedule until the player dies or summons the boss. All
 * curves are pure functions of seconds-over-limit so the headless tests can
 * pin them.
 *
 * Types are melee-only (firePattern: null): enrage timing is live-play-
 * dependent, so a firing enemy would need a kill-order pattern-stream key —
 * forbidden by the determinism split (same rule as splitter mites). The
 * spawner itself consumes only gameplayRng, never seeded streams.
 */
export const ENRAGE_TYPES: readonly EnemyType[] = [
  'shambler', 'shambler', 'husk', 'flanker', 'splitter',
];

/** Matches HARD_ENEMY_CAP: headroom under the hard 132-enemy test ceiling. */
export const ENRAGE_SPAWN_CAP = 126;

/** Seconds between enrage pulses: 2.5s at the crossing, floors at 0.6s. */
export function enrageSpawnInterval(secondsOver: number): number {
  return Math.max(0.6, 2.5 * Math.pow(0.75, secondsOver / 60));
}

/**
 * Enemies per pulse: 4 at the crossing, +2 per overtime minute, cap 12.
 * Doubled with the ×2 density rework (REGULAR_FILL halved — enrage kills
 * must keep their bar-fill parity, and the crescendo must outpace the horde).
 */
export function enragePackSize(secondsOver: number): number {
  return Math.min(12, 4 + 2 * Math.floor(secondsOver / 60));
}

/** Speed multiplier on enrage-spawned enemies: +10%/overtime min, cap +50%. */
export function enrageSpeedMult(secondsOver: number): number {
  return 1 + Math.min(0.5, 0.10 * (secondsOver / 60));
}
